/**
 * Resolves geolocation for cart flyout estimated shipping only.
 * Never writes to the basket — pass the result as `estimateAddress` to useShippingMethods.
 * When geo fails, `estimateAddress` stays null and useShippingMethods falls back to SFCC.
 */

import {useEffect, useRef, useState} from 'react';

import {getSiteConfig} from '@amblique/king-living/app/constants/site-config';
import {useGeocoding} from '@amblique/king-living/app/hooks/use-geocoding';
import {
  geoResultToShippingAddress,
  resolveBrowserGeoFull,
} from '@amblique/king-living/app/utils/browser-geo-address';
import {hasBasketShippingAddress} from '@amblique/king-living/app/utils/basket-tax-utils';

/**
 * @param {Object} options
 * @param {boolean} options.enabled - Resolve when true (e.g. cart flyout open)
 * @param {Object|null|undefined} options.basket - Current basket
 * @param {string|undefined|null} options.siteId
 * @param {string|undefined|null} options.geoApiKey - GOOGLE_GEOLOCATION_API_KEY site preference
 * @returns {{ estimateAddress: Object|null, isResolving: boolean }}
 */
export function useCartShippingEstimateAddress({
  enabled = false,
  basket,
  siteId,
  geoApiKey,
}) {
  const siteCountryCode = getSiteConfig(siteId)?.countryCode ?? 'AU';
  const basketHasAddress = hasBasketShippingAddress(basket);
  const {reverseGeocode} = useGeocoding();
  const reverseGeocodeRef = useRef(reverseGeocode);
  const geoAttemptedRef = useRef(false);

  const [estimateAddress, setEstimateAddress] = useState(null);
  const [isResolving, setIsResolving] = useState(false);

  useEffect(() => {
    reverseGeocodeRef.current = reverseGeocode;
  }, [reverseGeocode]);

  useEffect(() => {
    geoAttemptedRef.current = false;
    setEstimateAddress(null);
    setIsResolving(false);
  }, [siteId]);

  useEffect(() => {
    if (!enabled || basketHasAddress || typeof window === 'undefined') {
      if (basketHasAddress) {
        setEstimateAddress(null);
        setIsResolving(false);
      }

      return undefined;
    }

    if (geoAttemptedRef.current) {
      return undefined;
    }

    geoAttemptedRef.current = true;

    if (!geoApiKey) {
      return undefined;
    }

    let cancelled = false;

    setIsResolving(true);

    (async () => {
      try {
        const geo = await resolveBrowserGeoFull({
          siteId,
          apiKey: geoApiKey,
          reverseGeocode: reverseGeocodeRef.current,
        });

        if (cancelled) {
          return;
        }

        const next = geoResultToShippingAddress(geo, siteCountryCode);

        if (next) {
          setEstimateAddress(next);
        }
      } catch {
        // Geo failed — estimateAddress stays null; useShippingMethods uses SFCC fallback.
      } finally {
        if (!cancelled) {
          setIsResolving(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [basketHasAddress, enabled, geoApiKey, siteCountryCode, siteId]);

  return {
    estimateAddress: basketHasAddress ? null : estimateAddress,
    isResolving: enabled && !basketHasAddress && isResolving,
  };
}

export default useCartShippingEstimateAddress;
